import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeliveryHeader from './deliverydriver/DeliveryHeader';
import DeliveryMap from './deliverydriver/DeliveryMap';
import DeliveryActions from './deliverydriver/DeliveryActions';
import SubmitButton from './deliverydriver/SubmitButton';
import {
  PaymentModal,
  NoteModal,
  StatusModal,
  CameraModal,
  InfoModal,
  ContactModal,
  QRModal,
  ChatModal,
  EmergencyAlertModal
} from './deliverydriver/modals';
import { useDriverLocation } from './deliverydriver/hooks/useDriverLocation';
import { formatArrivalTime } from './deliverydriver/utils/timeUtils';

function DeliveryDriver({ delivery, onBack, setActiveComponent, driverId }) {
  const [liveDelivery, setLiveDelivery] = useState(delivery);
  const [note, setNote] = useState('');
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState('');
  
  // Payment states - now with amounts instead of booleans
  const [cashAmount, setCashAmount] = useState(0);
  const [fastmaxTransferAmount, setFastmaxTransferAmount] = useState(0);
  const [providerTransferAmount, setProviderTransferAmount] = useState(0);
  
  // Modal states
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [openCameraModal, setOpenCameraModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openContactModal, setOpenContactModal] = useState(false);
  const [openQRModal, setOpenQRModal] = useState(false);
  const [openChatModal, setOpenChatModal] = useState(false);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [sendingThreeMin, setSendingThreeMin] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);

  // Custom hooks
  const activeDelivery = liveDelivery || delivery;
  const driver = useDriverLocation(activeDelivery?.driver_id);

  // Format arrival time
  const formattedArrivalTime = activeDelivery?.arrival_time 
    ? formatArrivalTime(activeDelivery.arrival_time)
    : '';

  useEffect(() => {
    setLiveDelivery(delivery);
  }, [delivery]);

  useEffect(() => {
    if (!delivery?.id || !driverId) return undefined;

    let stopped = false;
    let timer;
    let inFlight = false;
    const controller = new AbortController();

    const fetchDelivery = async () => {
      if (stopped || inFlight) return;
      inFlight = true;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/delivery_points/driver/${driverId}/${delivery.id}`,
          { signal: controller.signal }
        );

        if (!stopped) {
          setLiveDelivery(response.data);
        }
      } catch (error) {
        if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
          console.error('Error refreshing delivery point:', error);
        }
      } finally {
        inFlight = false;
        if (!stopped) {
          timer = setTimeout(fetchDelivery, 10000);
        }
      }
    };

    fetchDelivery();

    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [delivery?.id, driverId]);

  // Reset cash when status changes to cancelado/rechazado
  useEffect(() => {
    if (status === 'cancelado' || status === 'rechazado') {
      setCashAmount(0);
      setFastmaxTransferAmount(0);
      setProviderTransferAmount(0);
    }
  }, [status]);

  const handleThreeMinutesClick = async () => {
    if (!activeDelivery?.id) return;
    
    try {
      setSendingThreeMin(true);
      const token = localStorage.getItem('token');

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/delivery/three_minutes`,
        { delivery_id: activeDelivery.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Mensaje de 3 minutos enviado.');
    } catch (err) {
      const status = err?.response?.status;
      const reason = err?.response?.data?.error || err.message;
      
      if (status === 428) {
        alert('No se pudo enviar: la sesión de WhatsApp está cerrada.');
      } else if (status === 404) {
        alert('Entrega no encontrada.');
      } else {
        alert(`Error enviando mensaje: ${reason}`);
      }
    } finally {
      setSendingThreeMin(false);
    }
  };

  const handleAlertSubmit = async ({ alert_type, message }) => {
    if (!activeDelivery?.id || !alert_type) return false;

    try {
      setSendingAlert(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/delivery-alerts`,
        {
          delivery_point_id: activeDelivery.id,
          driver_id: driverId || activeDelivery.driver_id,
          alert_type,
          message,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );

      alert('Alerta enviada en tiempo real.');
      return true;
    } catch (error) {
      const reason = error?.response?.data?.error || error.message;
      alert(`No se pudo enviar la alerta: ${reason}`);
      return false;
    } finally {
      setSendingAlert(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!note || !status) {
      alert('Por favor proporcione una nota y seleccione un estado antes de enviar.');
      return;
    }

    const totalCharged = cashAmount + fastmaxTransferAmount + providerTransferAmount;
    
    if (activeDelivery.charge && totalCharged === 0 && status === 'entregado') {
      alert('Por favor ingrese el monto cobrado.');
      return;
    }

    if (images.length === 0) {
      alert('Por favor agregue al menos una imagen.');
      return;
    }

    const formData = new FormData();
    formData.append('note', note);
    formData.append('status', status);
    formData.append('charged', cashAmount);
    formData.append('fastmax_transfer_amount', fastmaxTransferAmount);
    formData.append('provider_transfer_amount', providerTransferAmount);
    
    images.forEach((image, index) => {
      formData.append('images', image, `image-${index}.jpg`);
    });

    setLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/driver_delivery_submit/${activeDelivery.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('¡Entrega completada exitosamente!');
      onBack();
    } catch (error) {
      console.error('Error submitting delivery:', error);
      alert('Hubo un error al enviar la entrega. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const totalCharged = cashAmount + fastmaxTransferAmount + providerTransferAmount;

  return (
    <div className="relative h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <DeliveryHeader
        delivery={activeDelivery}
        formattedArrivalTime={formattedArrivalTime}
        totalCharged={totalCharged}
        onBack={onBack}
        setActiveComponent={setActiveComponent}
      />

      {/* Map Container */}
      <div className="flex-1 relative">
        <DeliveryMap
          delivery={activeDelivery}
          driver={driver}
        />

        {/* Action Buttons Overlay */}
        <DeliveryActions
          delivery={activeDelivery}
          hasNote={!!note}
          hasImages={images.length > 0}
          hasStatus={!!status}
          hasPayment={totalCharged > 0}
          sendingThreeMin={sendingThreeMin}
          onInfoClick={() => setOpenInfoModal(true)}
          onContactClick={() => setOpenContactModal(true)}
          onThreeMinClick={handleThreeMinutesClick}
          onQRClick={() => setOpenQRModal(true)}
          onDirectionsClick={() => {
            const destinationLatitude = activeDelivery.latitude ?? activeDelivery.provisional_latitude;
            const destinationLongitude = activeDelivery.longitude ?? activeDelivery.provisional_longitude;

            if (!destinationLatitude || !destinationLongitude) {
              alert('Esta entrega aun no tiene una ubicacion disponible.');
              return;
            }

            const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destinationLatitude},${destinationLongitude}`;
            window.open(googleMapsUrl, '_blank');
          }}
          onChatClick={() => setOpenChatModal(true)}
          onAlertClick={() => setOpenAlertModal(true)}
          onPaymentClick={() => setOpenPaymentModal(true)}
          onNoteClick={() => setOpenNoteModal(true)}
          onCameraClick={() => setOpenCameraModal(true)}
          onStatusClick={() => setOpenStatusModal(true)}
        />

        {/* Submit Button */}
        <SubmitButton
          loading={loading}
          onClick={handleSubmit}
        />
      </div>

      {/* Modals */}
      <PaymentModal
        open={openPaymentModal}
        onClose={() => setOpenPaymentModal(false)}
        cashAmount={cashAmount}
        fastmaxTransferAmount={fastmaxTransferAmount}
        providerTransferAmount={providerTransferAmount}
        onCashChange={setCashAmount}
        onFastmaxChange={setFastmaxTransferAmount}
        onProviderChange={setProviderTransferAmount}
        deliveryCharge={activeDelivery?.charge || 0}
      />

      <NoteModal
        open={openNoteModal}
        onClose={() => setOpenNoteModal(false)}
        note={note}
        onNoteChange={setNote}
      />

      <StatusModal
        open={openStatusModal}
        onClose={() => setOpenStatusModal(false)}
        status={status}
        onStatusChange={setStatus}
      />

      <CameraModal
        open={openCameraModal}
        onClose={() => setOpenCameraModal(false)}
        images={images}
        onImagesChange={setImages}
      />

      <InfoModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        delivery={activeDelivery}
      />

      <ContactModal
        open={openContactModal}
        onClose={() => setOpenContactModal(false)}
        delivery={activeDelivery}
      />

      <QRModal
        open={openQRModal}
        onClose={() => setOpenQRModal(false)}
      />

      <ChatModal
        open={openChatModal}
        onClose={() => setOpenChatModal(false)}
        delivery={activeDelivery}
        driverId={driverId}
      />

      <EmergencyAlertModal
        open={openAlertModal}
        onClose={() => setOpenAlertModal(false)}
        onSubmit={handleAlertSubmit}
        loading={sendingAlert}
      />

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-700 font-medium">Enviando entrega...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryDriver;
